import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { biomes } from "@shared/schema";
import {
  CheckCircle2,
  Instagram,
  MapPin,
  Leaf,
  Mail,
  Home,
  Twitter,
  Facebook,
  Heart,
} from "lucide-react";
import { motion } from "framer-motion";

const stateNames: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "Washington D.C."
};

interface WaitlistInfo {
  name: string;
  email: string;
  state: string;
  biomePreference?: string;
  position: number;
}

export default function WaitlistSuccessPage() {
  const [waitlistInfo, setWaitlistInfo] = useState<WaitlistInfo | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("name");
    const email = params.get("email");
    const state = params.get("state");
    const biomePreference = params.get("biome");
    const position = params.get("position");

    if (name && email && state) {
      setWaitlistInfo({
        name,
        email,
        state,
        biomePreference: biomePreference || undefined,
        position: parseInt(position || "1", 10),
      });
    }

    const storedInfo = sessionStorage.getItem("waitlistInfo");
    if (storedInfo && !name) {
      try {
        const parsed = JSON.parse(storedInfo);
        setWaitlistInfo(parsed);
        sessionStorage.removeItem("waitlistInfo");
      } catch (e) {
        console.error("Error parsing stored waitlist info:", e);
      }
    }
  }, []);

  const getStateName = (stateCode: string) => {
    return stateNames[stateCode.toUpperCase()] || stateCode;
  };

  const getBiomeName = (biomeId?: string) => {
    if (!biomeId) return null;
    const biome = biomes.find((b) => b.id === biomeId);
    return biome?.name || biomeId;
  };

  const stateName = waitlistInfo ? getStateName(waitlistInfo.state) : "";
  const biomeName = waitlistInfo ? getBiomeName(waitlistInfo.biomePreference) : null;
  const firstName = waitlistInfo?.name?.split(" ")[0] || "Friend";

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-muted/30">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link href="/">
            <a className="font-serif text-2xl font-semibold tracking-tight" data-testid="link-home">
              Life Itself Studio
            </a>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-8"
          >
            <h1 className="font-serif text-4xl md:text-5xl font-semibold mb-4" data-testid="heading-success">
              Thank You{firstName !== "Friend" ? `, ${firstName}` : ""}!
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto" data-testid="text-notify-message">
              You're officially on our waitlist. We'll notify you when we expand to your area!
            </p>
          </motion.div>

          {waitlistInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="mb-8">
                <CardContent className="py-6 space-y-4">
                  <div className="bg-muted/50 rounded-lg p-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Your waitlist position</p>
                    <p className="font-serif text-4xl font-bold text-primary mb-1" data-testid="text-position">
                      #{waitlistInfo.position}
                    </p>
                    {stateName && (
                      <p className="text-sm text-muted-foreground">
                        for {stateName}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                      <Mail className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">Confirmation sent to</p>
                        <p className="text-sm font-medium truncate" data-testid="text-email">{waitlistInfo.email}</p>
                      </div>
                    </div>

                    {stateName && (
                      <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                        <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Your state</p>
                          <p className="text-sm font-medium" data-testid="text-state">{stateName}</p>
                        </div>
                      </div>
                    )}

                    {biomeName && (
                      <div className="flex items-center gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                        <Leaf className="h-5 w-5 text-primary flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Biome preference</p>
                          <p className="text-sm font-medium" data-testid="text-biome">{biomeName}</p>
                        </div>
                        <Badge variant="secondary" className="ml-auto">Saved</Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="mb-8">
              <CardContent className="py-8">
                <div className="text-center mb-6">
                  <Heart className="h-8 w-8 mx-auto mb-4 text-primary" />
                  <h3 className="font-serif text-xl font-semibold mb-2">
                    Refer a Friend
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Share Life Itself Studio with friends and help us grow our community!
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="share-twitter"
                    onClick={() => {
                      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent("I just joined the Life Itself Studio waitlist for handcrafted terrariums!")}&url=${encodeURIComponent(window.location.origin)}`;
                      window.open(url, "_blank", "width=600,height=400");
                    }}
                  >
                    <Twitter className="h-4 w-4" />
                    Twitter
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="share-facebook"
                    onClick={() => {
                      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`;
                      window.open(url, "_blank", "width=600,height=400");
                    }}
                  >
                    <Facebook className="h-4 w-4" />
                    Facebook
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card className="mb-8 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="py-8 text-center">
                <Instagram className="h-8 w-8 mx-auto mb-4 text-primary" />
                <h3 className="font-serif text-xl font-semibold mb-2">
                  Follow Our Journey
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Get terrarium inspiration, care tips, and be the first to see our latest creations!
                </p>
                <a
                  href="https://instagram.com/lifeitself.studio"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-instagram"
                >
                  <Button variant="outline" className="gap-2">
                    <Instagram className="h-4 w-4" />
                    @lifeitself.studio
                  </Button>
                </a>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center"
          >
            <Link href="/">
              <Button className="gap-2" data-testid="button-return-home">
                <Home className="h-4 w-4" />
                Return Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
